import React, { useContext, useEffect } from 'react';
import { styled, ThemeProvider } from '@mui/material/styles';
import { crearTema } from '../theme/theme';
import {
	Box,
	CssBaseline,
	GlobalStyles,
	IconButton,
	Toolbar,
	Typography,
	useMediaQuery
} from '@mui/material';

import MuiDrawer from '@mui/material/Drawer';
import MuiAppBar from '@mui/material/AppBar';
import Loading from './Loading';
import MenuIcon from '@mui/icons-material/Menu';
import { AvisosProvider } from './base';
import { getRuta } from '../helpers/Helpers';
import { DataContext } from '../context/DataContext';
import NavBarElements from './NavBarElements';
import RutaSegura from './RutaSegura';
import { Route, Routes, useLocation } from 'react-router-dom';
import AccountContainer from '../pages/account/AccountContainer';
import NavLinks from './links/NavLinks';
import HomeContainer from '../pages/home/HomeContainer';
import './estilos/estilos.css';
import { isEmpty } from 'lodash';

import MainLocations from '../pages/settings/locations/MainLocations';
import Municipalities from '../pages/settings/locations/Municipalities';
import { Communities } from '../pages/settings/locations/Communities';
import Unautorized from './Unautorized';
import MainInteresCorriente from '../pages/settings/interesCorriente/MainInteresCorriente';
import PeriodicidadList from '../pages/settings/Periodicidad/PeriodicidadList';
import TasaCambioList from '../pages/settings/tasaCambio/TasaCambioList';
import ClientList from '../pages/clients/ClientList';
import AddClient from '../pages/clients/AddClient';
import { DetalleClientContainer } from '../pages/clients/detalleCliente/DetalleClientContainer';
import CreditLIst from '../pages/credits/CreditLIst';
import AddCredit from '../pages/credits/AddCredit';
import CreditDetailsContainer from '../pages/credits/creditDetails/CreditDetailsContainer';
import Userlist from '../pages/security/users/Userlist';
import RolList from '../pages/security/rols/RolList';
import AddUser from '../pages/security/users/AddUser';
import EditUser from '../pages/security/users/EditUser';
import AddRol from '../pages/security/rols/AddRol';
import EditRol from '../pages/security/rols/EditRol';
import HolidayList from '../pages/settings/holidays/HolidayList';
import Reports from '../pages/reports/Reports';

import ClientesGestor from '../pages/reports/reportesCartera/ClientesGestor';
import CreditsByGestor from '../pages/reports/reportesCartera/CreditsByGestor';
import CobroDiario from '../pages/reports/reportesCartera/CobroDiario';
import Recuperacion from '../pages/reports/reportesCartera/Recuperacion';
import Desembolsos from '../pages/reports/reportesCartera/Desembolsos';
import SaldosCartera from '../pages/reports/reportesCartera/SaldosCartera';
import { getUser } from '../api/Account';
import DefeatedCredits from '../pages/reports/reportesCartera/DefeatedCredits';
import AccountStatus from '../pages/reports/reportesCartera/AccountStatus';
import CierreCaja from '../pages/reports/reportesCartera/CierreCaja';
import CierreCajaDetallado from '../pages/reports/reportesCartera/CierreCajaDetallado';
import EstadoResultado from '../pages/reports/reportesCartera/EstadoResultado';
import CreditsCancelled from '../pages/reports/reportesCartera/CreditsCancelled';
import VencimientosProyectados from '../pages/reports/reportesCartera/VencimientosProyectados';
import SaldosCarteraByGestor from '../pages/reports/reportesCartera/SaldosCarteraByGestor';
import AnalyticsWallet from '../pages/reports/reportesCartera/AnalyticsWallet';
import RecuperacionVencidos from '../pages/reports/reportesCartera/RecuperacionVencidos';
import BosqueMora from '../pages/reports/reportesCartera/BosqueMora';
import CargoList from '../pages/rrhh/cargos/CargoList';
import EmployeeList from '../pages/rrhh/employees/EmployeeList';
import AddEmployee from '../pages/rrhh/employees/AddEmployee';
import { EmployeDetailsContainer } from '../pages/rrhh/employees/detalleEmployee/EmployeDetailsContainer';
import NominaList from '../pages/rrhh/nomina/NominaList';
import NominaDetails from '../pages/rrhh/nomina/nominaDetails/NominaDetails';
import NominaEmployee from '../pages/rrhh/nomina/nominaDetails/NominaEmployee';
import ReporteUserTracking from '../pages/reports/reportesAdministrativos/ReporteUserTracking';
import PrintNomina from '../pages/rrhh/nomina/nominaDetails/PrintNomina';

const drawerWidth = 250;

// Quien pide reducir el movimiento no ve la animacion al abrir y cerrar el menu.
const sinMovimiento = { '@media (prefers-reduced-motion: reduce)': { transition: 'none' } };

const openedMixin = (theme) => ({
	width: drawerWidth,
	transition: theme.transitions.create('width', {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.enteringScreen
	}),
	overflowX: 'hidden',
	...sinMovimiento
});

const closedMixin = (theme) => ({
	transition: theme.transitions.create('width', {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.leavingScreen
	}),
	overflowX: 'hidden',
	// Solo el icono de cada enlace (ver links/styles.css).
	width: `calc(${theme.spacing(8)} + 1px)`,
	...sinMovimiento
});

// Menu de escritorio: permanente y colapsable. En el telefono se usa un drawer temporal.
const Drawer = styled(MuiDrawer, {
	shouldForwardProp: (prop) => prop !== 'open'
})(({ theme, open }) => ({
	width: drawerWidth,
	flexShrink: 0,
	whiteSpace: 'nowrap',
	boxSizing: 'border-box',
	...(open && {
		...openedMixin(theme),
		'& .MuiDrawer-paper': openedMixin(theme)
	}),
	...(!open && {
		...closedMixin(theme),
		'& .MuiDrawer-paper': closedMixin(theme)
	})
}));

export default function MiniDrawer() {
	const { isDarkMode, access, docTitle, version, icon, setIsLogged } = useContext(DataContext);
	const [open, setOpen] = React.useState(false);
	const [abiertoMovil, setAbiertoMovil] = React.useState(false);
	const { pathname } = useLocation();

	const user = getUser();

	let rout = getRuta();

	// El theme vive en src/theme (opcion A "Marino"); se crea una vez por modo, no en cada render.
	const tema = crearTema(isDarkMode ? 'dark' : 'light');

	// Menos de 900 px (xs y sm): drawer temporal que se abre con el boton de menu.
	const esMovil = useMediaQuery(tema.breakpoints.down('md'), { noSsr: true });

	// El menu del telefono se cierra al navegar y al pasar a escritorio.
	useEffect(() => {
		setAbiertoMovil(false);
	}, [pathname, esMovil]);

	if (isEmpty(access)) {
		return <Loading />;
	}

	if (user === null) {
		setIsLogged(false);
		return;
	}

	const menuAbierto = esMovil ? abiertoMovil : open;
	// En el telefono el menu siempre muestra los nombres; en escritorio, solo si esta abierto.
	const conNombres = esMovil || open;

	const alternarMenu = () => {
		if (esMovil) {
			setAbiertoMovil(!abiertoMovil);
		} else {
			setOpen(!open);
		}
	};

	// Logo y nombre de la app: van en la barra superior y, en el telefono, arriba del drawer
	// temporal (el modal tapa la barra).
	const marca = (
		<>
			<Box
				component="img"
				src={icon}
				alt={`Logo de ${docTitle}`}
				sx={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'contain' }}
			/>
			<Typography
				component="p"
				variant="h6"
				noWrap
				sx={{ fontWeight: 700, color: 'primary.main' }}
			>
				{docTitle}
			</Typography>
		</>
	);

	const contenidoMenu = (
		<>
			{/* Espacio para la barra superior, que va por encima del menu en escritorio */}
			{esMovil ? <Toolbar sx={{ gap: 1.5 }}>{marca}</Toolbar> : <Toolbar />}
			{/* En el teléfono, tocar un enlace cierra el menú (también el de la página actual, que no cambia la ruta) */}
			<div
				onClick={(evento) => {
					if (esMovil && evento.target.closest('a')) setAbiertoMovil(false);
				}}
			>
				<NavLinks drawerIsOpen={conNombres} />
			</div>
			{conNombres && (
				<Typography variant="caption" sx={{ mt: 'auto', p: 2, color: 'text.secondary' }}>
					{version}
				</Typography>
			)}
		</>
	);

	return (
		<>
			<ThemeProvider theme={tema}>
				<AvisosProvider>
					<Box sx={{ display: 'flex' }}>
						<CssBaseline />
						{/* Color del contorno de foco (index.css): el primario del modo actual */}
						<GlobalStyles
							styles={(theme) => ({
								':root': { '--color-foco': theme.palette.primary.main }
							})}
						/>
						<MuiAppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
							<Toolbar sx={{ gap: 1.5 }}>
								<IconButton
									aria-label={menuAbierto ? 'Cerrar menú' : 'Abrir menú'}
									aria-expanded={menuAbierto}
									onClick={alternarMenu}
									edge="start"
									sx={{ color: 'text.secondary' }}
								>
									<MenuIcon />
								</IconButton>
								{marca}
								<Box sx={{ flexGrow: 1 }} />
								<NavBarElements />
							</Toolbar>
						</MuiAppBar>

						{esMovil ? (
							<MuiDrawer
								variant="temporary"
								open={abiertoMovil}
								onClose={() => setAbiertoMovil(false)}
								ModalProps={{ keepMounted: true }}
								sx={{
									'& .MuiDrawer-paper': {
										width: drawerWidth,
										maxWidth: '85vw',
										boxSizing: 'border-box'
									}
								}}
							>
								{contenidoMenu}
							</MuiDrawer>
						) : (
							<Drawer variant="permanent" open={open}>
								{contenidoMenu}
							</Drawer>
						)}

						<Box
							component="main"
							sx={{
								flexGrow: 1,
								minWidth: 0,
								p: { xs: 2, md: 3 },
								bgcolor: 'background.default',
								height: '100vh',
								overflow: 'auto'
							}}
						>
							{/* Espacio para la barra superior */}
							<Toolbar />
							<RutaSegura>
						<Routes>
							<Route path={`${rout}/`} element={<HomeContainer />} />
							{/* Rulas Clientes */}
							<Route path={`${rout}/clients`} element={<ClientList />} />
							<Route path={`${rout}/addclient`} element={<AddClient />} />
							<Route
								path={`${rout}/client/:id`}
								element={<DetalleClientContainer />}
							/>

							{/* Rulas Creditos */}
							<Route path={`${rout}/credits`} element={<CreditLIst />} />
							<Route path={`${rout}/credit/add/:id`} element={<AddCredit />} />
							<Route
								path={`${rout}/credit/:id`}
								element={<CreditDetailsContainer />}
							/>

							{/* Rulas Nomina */}
							<Route path={`${rout}/employees`} element={<EmployeeList />} />
							<Route path={`${rout}/add-employees`} element={<AddEmployee />} />
							<Route
								path={`${rout}/employee/:id`}
								element={<EmployeDetailsContainer />}
							/>
							<Route path={`${rout}/nomina`} element={<NominaList />} />
							<Route path={`${rout}/nominadetails/:id`} element={<NominaDetails />} />
							<Route
								path={`${rout}/nominadetails/:id/:ideployee`}
								element={<NominaEmployee />}
							/>
							<Route path={`${rout}/puesto`} element={<CargoList />} />
							<Route path={`${rout}/printNomina/:id`} element={<PrintNomina />} />

							{/* Rulas Reportes */}
							<Route path={`${rout}/reports`} element={<Reports />} />
							<Route
								path={`${rout}/r-clientsgestor/:selectedStatus/:selectedSortBy`}
								element={<ClientesGestor />}
							/>
							<Route
								path={`${rout}/r-creditsbyGestor/:selectedGestor/:selectedSortBy`}
								element={<CreditsByGestor />}
							/>
							<Route
								path={`${rout}/r-cobrodiario/:selectedGestor/:selectedFecha`}
								element={<CobroDiario />}
							/>
							<Route
								path={`${rout}/r-recuperacion/:selectedGestor/:fechaDesde/:fechaHasta`}
								element={<Recuperacion />}
							/>
							<Route
								path={`${rout}/r-desembolsos/:selectedGestor/:fechaDesde/:fechaHasta`}
								element={<Desembolsos />}
							/>
							<Route
								path={`${rout}/r-saldoscartera/:selectedGestor/:selectedSortBy`}
								element={<SaldosCartera />}
							/>
							<Route
								path={`${rout}/r-saldoscarterabygestor/:selectedGestor`}
								element={<SaldosCarteraByGestor />}
							/>
							<Route
								path={`${rout}/r-analyticswallet/:selectedGestor/:selectedSortBy`}
								element={<AnalyticsWallet />}
							/>
							<Route
								path={`${rout}/r-defeatedcredits/:selectedGestor/:selectedSortBy`}
								element={<DefeatedCredits />}
							/>
							<Route
								path={`${rout}/r-recuperacionvencidos/:selectedGestor/:selectedSortBy/:fechaDesde/:fechaHasta/:estado`}
								element={<RecuperacionVencidos />}
							/>

							<Route
								path={`${rout}/r-proyecteddefeatedcredits/:selectedGestor/:fechaDesde/:fechaHasta/:selectedSortBy`}
								element={<VencimientosProyectados />}
							/>

							<Route
								path={`${rout}/r-bosquemora/:selectedGestor`}
								element={<BosqueMora />}
							/>

							<Route
								path={`${rout}/r-accountstatus/:selectedCredit`}
								element={<AccountStatus />}
							/>
							<Route
								path={`${rout}/r-cierrecajad/:fecha`}
								element={<CierreCajaDetallado />}
							/>
							<Route path={`${rout}/r-cierrecaja/:fecha`} element={<CierreCaja />} />
							<Route
								path={`${rout}/r-estadoresultado/:fechaDesde/:fechaHasta`}
								element={<EstadoResultado />}
							/>

							<Route
								path={`${rout}/r-creditscancelled/:selectedGestor/:fechaDesde/:fechaHasta/:justNoReNew`}
								element={<CreditsCancelled />}
							/>

							<Route
								path={`${rout}/r-usertracking/:selectedGestor/:selectedFecha`}
								element={<ReporteUserTracking />}
							/>

							{/* Rulas Security-users */}
							<Route path={`${rout}/users`} element={<Userlist />} />
							<Route path={`${rout}/adduser`} element={<AddUser />} />
							<Route path={`${rout}/user/:id`} element={<EditUser />} />

							{/* Rulas Security-rols */}
							<Route path={`${rout}/rols`} element={<RolList />} />
							<Route path={`${rout}/addrol`} element={<AddRol />} />
							<Route path={`${rout}/rol/:id`} element={<EditRol />} />

							{/* Rulas Security-server */}
							{/* <Route path={`${rout}/addrol`} element={<AddRol />} />
              <Route path={`${rout}/rol/:id`} element={<EditRol />} /> */}

							<Route path={`${rout}/account`} element={<AccountContainer />} />

							{/* Rulas Ubicaciones */}
							<Route path={`${rout}/locations`} element={<MainLocations />} />
							<Route path={`${rout}/department/:id`} element={<Municipalities />} />
							<Route
								path={`${rout}/locations/municipalities/:id`}
								element={<Communities />}
							/>
							<Route
								path={`${rout}/locations/municipalities/:id`}
								element={<Communities />}
							/>

							<Route
								path={`${rout}/tasa-interes`}
								element={<MainInteresCorriente />}
							/>
							<Route path={`${rout}/periodicidad`} element={<PeriodicidadList />} />
							<Route path={`${rout}/holidays`} element={<HolidayList />} />
							<Route path={`${rout}/tasa-cambio`} element={<TasaCambioList />} />
						</Routes>
							</RutaSegura>
						</Box>
					</Box>
				</AvisosProvider>
				<Loading />
			</ThemeProvider>
			<Unautorized />
		</>
	);
}
